import React, { useState, useCallback, useRef } from "react";
import { getPdfjs } from "@/lib/pdfjs-config";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, CheckCircle, XCircle, Image as ImageIcon, Upload } from "lucide-react";

// DB schema uses snake_case
interface RenderedPageDB {
  page_number: number;
  storage_path: string;
  width: number;
  height: number;
}

// Internal rendering uses camelCase
interface RenderedPage {
  pageNumber: number;
  storagePath: string;
  width: number;
  height: number;
  imageUrl?: string;
}

type RenderPhase = "idle" | "downloading" | "rendering" | "uploading" | "updating" | "complete" | "error" | "reupload";

interface StagingItem {
  id: string;
  batch_run_id: string;
  merchant_key: string;
  evidence_json: {
    // CR168: Unified bucket/key schema for PDF location
    flyer_bucket?: string;        // Storage bucket name
    flyer_storage_key?: string;   // Storage object key
    // Legacy field (deprecated - kept for backwards compatibility)
    flyer_storage_path?: string;
    flyer_filename?: string;
    render_status?: string;
    flyer_pages?: RenderedPageDB[];
    [key: string]: unknown;
  };
}

interface FlyerRenderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stagingItem: StagingItem | null;
  onComplete?: () => void;
}

export function FlyerRenderDialog({
  open,
  onOpenChange,
  stagingItem,
  onComplete,
}: FlyerRenderDialogProps) {
  const [phase, setPhase] = useState<RenderPhase>("idle");
  const [progress, setProgress] = useState(0);
  const [renderedPages, setRenderedPages] = useState<RenderedPage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [diagnostics, setDiagnostics] = useState<{
    pdfBytes?: number;
    pageCount?: number;
    uploadedCount?: number;
    errors?: string[];
  }>({});
  const [reuploadFile, setReuploadFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetState = useCallback(() => {
    setPhase("idle");
    setProgress(0);
    setRenderedPages([]);
    setError(null);
    setDiagnostics({});
    setReuploadFile(null);
  }, []);

  const renderAndUpload = useCallback(async () => {
    if (!stagingItem) return;

    const evidence = stagingItem.evidence_json;
    
    // CR168: Use explicit bucket/key if available, fallback to legacy path
    const bucketName = evidence?.flyer_bucket || "flyer-fetch-assets";
    const pdfPathRaw = evidence?.flyer_storage_key || evidence?.flyer_storage_path;

    if (!pdfPathRaw) {
      setError("No PDF storage key found in evidence_json (flyer_storage_key or flyer_storage_path)");
      setPhase("error");
      return;
    }

    console.log(`[FlyerRenderDialog] Using bucket=${bucketName}, key=${pdfPathRaw}`);

    // Normalize path: strip any full URL prefix if present
    let pdfPath = pdfPathRaw;
    
    // Handle full URLs (e.g., https://xxx.supabase.co/storage/v1/object/public/bucket/path)
    const publicUrlMatch = pdfPathRaw.match(/\/storage\/v1\/object\/(?:public|sign)\/[^/]+\/(.+)/);
    if (publicUrlMatch) {
      pdfPath = decodeURIComponent(publicUrlMatch[1]);
    }
    
    // Remove leading slash if present
    if (pdfPath.startsWith("/")) {
      pdfPath = pdfPath.slice(1);
    }

    console.log("[FlyerRenderDialog] Starting render:", {
      bucketName,
      pdfPathRaw,
      pdfPathNormalized: pdfPath,
      stagingItemId: stagingItem.id,
      merchantKey: stagingItem.merchant_key,
    });

    try {
      // Phase 1: Download PDF
      setPhase("downloading");
      setProgress(5);

      // First, verify the file exists by listing the parent folder
      const pathParts = pdfPath.split("/");
      const fileName = pathParts.pop() || "";
      const folderPath = pathParts.join("/");
      
      console.log("[FlyerRenderDialog] Checking file existence:", { folderPath, fileName });
      
      const { data: listData, error: listError } = await supabase.storage
        .from(bucketName)
        .list(folderPath);
      
      if (listError) {
        console.error("[FlyerRenderDialog] Failed to list folder:", listError);
        throw new Error(`Failed to access storage folder '${folderPath}': ${listError.message}. Make sure you are logged in as an admin.`);
      }
      
      const fileExists = listData?.some(f => f.name === fileName);
      console.log("[FlyerRenderDialog] Folder contents:", {
        folderPath,
        fileCount: listData?.length ?? 0,
        files: listData?.map(f => f.name).slice(0, 10),
        targetFile: fileName,
        fileExists,
      });
      
      if (!fileExists) {
        // Set phase to allow re-upload instead of failing
        setPhase("reupload");
        setError(`PDF not found in storage at '${pdfPath}'. The file was not uploaded successfully during batch creation. You can re-upload the PDF below.`);
        return;
      }

      // CR083: Get signed URL with longer expiration (15 min) for large PDFs
      const { data: signedUrlData, error: signedUrlError } = await supabase.storage
        .from(bucketName)
        .createSignedUrl(pdfPath, 900); // 15 minutes instead of 5

      if (signedUrlError || !signedUrlData?.signedUrl) {
        console.error("[FlyerRenderDialog] Signed URL error:", signedUrlError);
        throw new Error(`Failed to create signed URL: ${signedUrlError?.message || "Unknown error"}`);
      }
      
      console.log("[FlyerRenderDialog] Signed URL created successfully (15 min expiry)");

      // CR083: Preflight check - verify PDF is accessible before rendering
      const preflightResponse = await fetch(signedUrlData.signedUrl, { method: "HEAD" });
      if (!preflightResponse.ok) {
        const httpError = `HTTP ${preflightResponse.status}: ${preflightResponse.statusText}`;
        console.error("[FlyerRenderDialog] Preflight check failed:", httpError);
        
        // Provide specific error messages for common issues
        let userMessage = `PDF access failed: ${httpError}`;
        if (preflightResponse.status === 403) {
          userMessage = "PDF access denied (403 Forbidden). The signed URL may have expired or the file permissions changed.";
        } else if (preflightResponse.status === 404) {
          userMessage = "PDF not found (404). The file may have been deleted from storage.";
        } else if (preflightResponse.status === 401) {
          userMessage = "Authentication failed (401). Please refresh and try again.";
        }
        
        setPhase("reupload");
        setError(userMessage);
        return;
      }
      
      console.log("[FlyerRenderDialog] Preflight check passed, fetching PDF...");

      // Fetch the PDF
      const response = await fetch(signedUrlData.signedUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
      }

      const pdfBytes = await response.arrayBuffer();
      setDiagnostics(prev => ({ ...prev, pdfBytes: pdfBytes.byteLength }));
      setProgress(20);
      setPhase("rendering");

      let pdf: Awaited<ReturnType<Awaited<ReturnType<typeof getPdfjs>>["getDocument"]>["promise"]>;
      try {
        const pdfjsLib = await getPdfjs();
        const loadingTask = pdfjsLib.getDocument({ data: pdfBytes });
        pdf = await loadingTask.promise;
        console.log(`[FlyerRenderDialog] PDF parsed, ${pdf.numPages} pages`);
      } catch (parseErr) {
        console.error("[FlyerRenderDialog] PDF parse error:", parseErr);
        throw new Error(`PDF parse failed: ${parseErr instanceof Error ? parseErr.message : "Unknown error"}`);
      }
      
      const pageCount = pdf.numPages;
      setDiagnostics(prev => ({ ...prev, pageCount }));

      const pages: RenderedPage[] = [];
      const renderErrors: string[] = [];

      for (let i = 1; i <= pageCount; i++) {
        try {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 2.0 }); // Higher quality

          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const ctx = canvas.getContext("2d");

          if (!ctx) {
            renderErrors.push(`Page ${i}: Failed to get canvas context`);
            continue;
          }

          await page.render({ canvasContext: ctx, viewport }).promise;

          // Convert to JPEG blob
          const blob = await new Promise<Blob | null>((resolve) => {
            canvas.toBlob(resolve, "image/jpeg", 0.85);
          });

          if (!blob) {
            renderErrors.push(`Page ${i}: Failed to convert to JPEG`);
            continue;
          }

          pages.push({
            pageNumber: i,
            storagePath: "", // Will be set after upload
            width: viewport.width,
            height: viewport.height,
          });

          // Store blob temporarily for upload
          (pages[pages.length - 1] as RenderedPage & { blob?: Blob }).blob = blob;

          setProgress(20 + Math.floor((i / pageCount) * 30));
        } catch (pageError) {
          renderErrors.push(`Page ${i}: ${pageError instanceof Error ? pageError.message : "Unknown error"}`);
        }
      }

      if (pages.length === 0) {
        throw new Error(`Failed to render any pages. Errors: ${renderErrors.join("; ")}`);
      }

      setDiagnostics(prev => ({ ...prev, errors: renderErrors }));
      setProgress(50);

      // Phase 3: Upload rendered images
      setPhase("uploading");
      const uploadedPages: RenderedPage[] = [];

      for (let i = 0; i < pages.length; i++) {
        const page = pages[i] as RenderedPage & { blob?: Blob };
        if (!page.blob) continue;

        const storagePath = `rendered/${stagingItem.batch_run_id}/${stagingItem.id}/page_${page.pageNumber}.jpg`;

        const { error: uploadError } = await supabase.storage
          .from("flyer-fetch-assets")
          .upload(storagePath, page.blob, {
            contentType: "image/jpeg",
            upsert: true,
          });

        if (uploadError) {
          renderErrors.push(`Upload page ${page.pageNumber}: ${uploadError.message}`);
          continue;
        }

        uploadedPages.push({
          pageNumber: page.pageNumber,
          storagePath,
          width: page.width,
          height: page.height,
        });

        setProgress(50 + Math.floor(((i + 1) / pages.length) * 30));
      }

      if (uploadedPages.length === 0) {
        throw new Error(`Failed to upload any pages. Errors: ${renderErrors.join("; ")}`);
      }

      setDiagnostics(prev => ({ ...prev, uploadedCount: uploadedPages.length, errors: renderErrors }));
      setRenderedPages(uploadedPages);
      setProgress(80);

      // Phase 4: Update staging row with render manifest
      setPhase("updating");

      const currentEvidence = stagingItem.evidence_json || {};

      const { error: updateError } = await supabase
        .from("cr075_staging_deal_items")
        .update({
          evidence_json: {
            ...currentEvidence,
            flyer_pages: uploadedPages.map(p => ({
              page_number: p.pageNumber,
              storage_path: p.storagePath,
              width: p.width,
              height: p.height,
            })),
            render_status: "RENDERED",
            page_count: uploadedPages.length,
            rendered_at: new Date().toISOString(),
          },
        })
        .eq("id", stagingItem.id);

      if (updateError) {
        throw new Error(`Failed to update staging row: ${updateError.message}`);
      }

      // CR456: Persist truth rows via edge function (RLS-safe, service role)
      try {
        const { error: truthErr } = await supabase.functions.invoke(
          "persist-flyer-rendered-pages",
          {
            body: {
              batch_run_id: stagingItem.batch_run_id,
              flyer_merchant: stagingItem.merchant_key,
              render_source: "CLIENT_RENDER",
              pages: uploadedPages.map(p => ({
                page_number: p.pageNumber,
                storage_path: p.storagePath,
                width: p.width,
                height: p.height,
              })),
            },
          }
        );
        if (truthErr) {
          console.error("[FlyerRenderDialog] CR456: truth persistence failed:", truthErr);
          // Surface error but don't block — staging is already updated
          setDiagnostics(prev => ({
            ...prev,
            errors: [...(prev.errors || []), `Truth write failed: ${truthErr.message}`],
          }));
        } else {
          console.log(`[FlyerRenderDialog] CR456: Persisted ${uploadedPages.length} truth rows via edge function`);
        }
      } catch (truthCallErr) {
        console.error("[FlyerRenderDialog] CR456: truth edge function call error:", truthCallErr);
      }

      setProgress(100);
      setPhase("complete");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error occurred");
      setPhase("error");
    }
  }, [stagingItem]);

  // Handle file re-upload when PDF wasn't found
  const handleReupload = useCallback(async () => {
    if (!stagingItem || !reuploadFile) return;
    
    const pdfPath = stagingItem.evidence_json?.flyer_storage_path;
    if (!pdfPath) return;
    
    setPhase("uploading");
    setProgress(10);
    setError(null);
    
    try {
      console.log("[FlyerRenderDialog] Re-uploading PDF to:", pdfPath);
      
      const { error: uploadError } = await supabase.storage
        .from("flyer-fetch-assets")
        .upload(pdfPath, reuploadFile, {
          contentType: "application/pdf",
          upsert: true,
        });
      
      if (uploadError) {
        throw new Error(`Failed to upload PDF: ${uploadError.message}`);
      }
      
      console.log("[FlyerRenderDialog] Re-upload successful, now rendering...");
      setReuploadFile(null);
      
      // Now try rendering again
      await renderAndUpload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
      setPhase("error");
    }
  }, [stagingItem, reuploadFile, renderAndUpload]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type === "application/pdf") {
      setReuploadFile(file);
      setError(null);
    }
  }, []);

  const handleClose = useCallback(() => {
    if (phase === "complete") {
      onComplete?.();
    }
    resetState();
    onOpenChange(false);
  }, [phase, onComplete, resetState, onOpenChange]);

  const getPhaseLabel = (p: RenderPhase): string => {
    switch (p) {
      case "idle": return "Ready";
      case "downloading": return "Downloading PDF...";
      case "rendering": return "Rendering pages...";
      case "uploading": return "Uploading images...";
      case "updating": return "Updating database...";
      case "complete": return "Complete!";
      case "reupload": return "PDF Missing";
      case "error": return "Error";
      default: return "Unknown";
    }
  };

  if (!stagingItem) return null;

  const evidence = stagingItem.evidence_json;
  const filename = evidence?.flyer_filename || evidence?.flyer_storage_path?.split("/").pop() || "Unknown file";

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Render Flyer Pages
          </DialogTitle>
          <DialogDescription>
            Convert PDF pages to images for OCR processing
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* File info */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">File:</span>
              <code className="bg-muted px-2 py-1 rounded text-xs">{filename}</code>
            </div>
            <div className="flex items-start gap-2 text-sm">
              <span className="text-muted-foreground shrink-0">Storage Path:</span>
              <code className="bg-muted px-2 py-1 rounded text-xs break-all">
                {evidence?.flyer_storage_path || "(not set)"}
              </code>
            </div>
          </div>

          {/* Progress */}
          {phase !== "idle" && phase !== "error" && phase !== "reupload" && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>{getPhaseLabel(phase)}</span>
                <span className="text-muted-foreground">{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          )}

          {/* Re-upload section for missing PDFs */}
          {phase === "reupload" && (
            <div className="bg-amber-500/10 border border-amber-500/20 p-4 rounded-md space-y-3">
              <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 font-medium text-sm">
                <Upload className="h-4 w-4" />
                PDF Not Found - Re-upload Required
              </div>
              <p className="text-xs text-muted-foreground">
                {error}
              </p>
              <div className="space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  onChange={handleFileChange}
                  className="text-xs"
                />
                {reuploadFile && (
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      {reuploadFile.name} ({(reuploadFile.size / 1024).toFixed(1)} KB)
                    </Badge>
                    <Button size="sm" onClick={handleReupload}>
                      <Upload className="h-3 w-3 mr-1" />
                      Upload & Render
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Error display */}
          {phase === "error" && error && (
            <div className="bg-destructive/10 text-destructive p-3 rounded-md text-sm space-y-2">
              <div className="flex items-center gap-2 font-medium">
                <XCircle className="h-4 w-4" />
                Render Failed
              </div>
              <p className="text-xs whitespace-pre-wrap break-words">{error}</p>
              <p className="text-xs text-muted-foreground mt-2">
                Check browser console (F12) for detailed diagnostics.
              </p>
            </div>
          )}

          {/* Success display */}
          {phase === "complete" && (
            <div className="bg-green-500/10 text-green-700 dark:text-green-400 p-3 rounded-md text-sm">
              <div className="flex items-center gap-2 font-medium mb-1">
                <CheckCircle className="h-4 w-4" />
                Rendering Complete
              </div>
              <p className="text-xs">
                {renderedPages.length} pages rendered and uploaded successfully.
              </p>
            </div>
          )}

          {/* Diagnostics */}
          {(diagnostics.pageCount || diagnostics.uploadedCount) && (
            <div className="text-xs text-muted-foreground space-y-1 border-t pt-3">
              {diagnostics.pdfBytes && (
                <div>PDF size: {(diagnostics.pdfBytes / 1024).toFixed(1)} KB</div>
              )}
              {diagnostics.pageCount && (
                <div>Pages in PDF: {diagnostics.pageCount}</div>
              )}
              {diagnostics.uploadedCount && (
                <div>Pages uploaded: {diagnostics.uploadedCount}</div>
              )}
              {diagnostics.errors && diagnostics.errors.length > 0 && (
                <div className="text-amber-600">
                  Warnings: {diagnostics.errors.length}
                </div>
              )}
            </div>
          )}

          {/* Page previews */}
          {renderedPages.length > 0 && (
            <div className="border-t pt-3">
              <div className="text-sm font-medium mb-2">Rendered Pages</div>
              <div className="flex flex-wrap gap-2">
                {renderedPages.map((page) => (
                  <Badge key={page.pageNumber} variant="secondary">
                    Page {page.pageNumber} ({page.width}x{page.height})
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          {phase === "idle" && (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={renderAndUpload}>
                <ImageIcon className="h-4 w-4 mr-2" />
                Render Pages
              </Button>
            </>
          )}
          {(phase === "downloading" || phase === "rendering" || phase === "uploading" || phase === "updating") && (
            <Button disabled>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Processing...
            </Button>
          )}
          {phase === "reupload" && (
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
          )}
          {(phase === "complete" || phase === "error") && (
            <Button onClick={handleClose}>
              {phase === "complete" ? "Done" : "Close"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
